import { AlertTriangle } from 'lucide-react';
import { cn } from '../../lib/cn';
import { Button } from './Button';
import { Card } from './Card';

export interface ErrorStateProps {
  title?: string;
  message?: string;
  onRetry?: () => void;
  retryLabel?: string;
  /**
   * Wrap the state in a Card for standalone placement. Leave false (default)
   * when it renders inside an existing Card.
   */
  card?: boolean;
  className?: string;
}

/**
 * Query/fetch failure state with retry — never a blank div (BRIEF).
 * Replaces the per-feature ErrorCard / QueryError implementations.
 */
export function ErrorState({
  title = 'Something went wrong',
  message,
  onRetry,
  retryLabel = 'Try again',
  card = false,
  className,
}: ErrorStateProps) {
  const body = (
    <div
      className={cn(
        'flex flex-col items-center justify-center px-6 py-14 text-center',
        !card && className,
      )}
    >
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-status-late-bg text-status-late">
        <AlertTriangle className="h-6 w-6" aria-hidden />
      </div>
      <h3 className="text-heading-4 text-secondary">{title}</h3>
      <p className="mt-1 max-w-sm text-body-sm text-grey-500">
        {message ?? 'The request failed. Check your connection and try again.'}
      </p>
      {onRetry && (
        <Button variant="outline" size="sm" className="mt-5" onClick={onRetry}>
          {retryLabel}
        </Button>
      )}
    </div>
  );

  return card ? <Card className={className}>{body}</Card> : body;
}
